<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay
      :show="[STATES.CREATING, STATES.GETTING_PRICES].some(state.matches)"
      class="z-20"
    />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="[STATES.CREATE_ERROR].some(state.matches)"
            :api-response-error="formError"
            class="mb-8"
          />

          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactPage">
              <TText tag="h1" size="lg" variant="semibold" classes="mb-2">
                {{
                  t('views.cottage-contact.title', {
                    name: cottageContactPage.name,
                  })
                }}
              </TText>
              <TText
                v-if="
                  cottageContactPage.owner.answer &&
                  cottageContactPage.owner.answer.rate !== null
                "
              >
                {{
                  cottageContactPage.owner.answer.rate > 0
                    ? t('views.cottage-contact.answerRate', {
                        rate: cottageContactPage.owner.answer.rate,
                        range: t(
                          `range.${cottageContactPage.owner.answer.range}`,
                        ),
                        value: cottageContactPage.owner.answer.value,
                        unit: t(
                          `time.${cottageContactPage.owner.answer.unit}`,
                          cottageContactPage.owner.answer.value,
                        ),
                      })
                    : t('views.cottage-contact.noAnswerRate')
                }}
              </TText>

              <TSeparator class="mt-6 mb-8" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactPage">
              <UCard :ui="publicCard">
                <TText variant="semibold" classes="mb-2">
                  {{ t('views.cottage-contact.price.message') }}
                </TText>
                <CottageContactPrice
                  :item="cottageContactPage"
                  @submit="onPriceSubmit"
                  @update-dates="onPriceUpdateDates"
                  @update-people="onPriceUpdatePeople"
                />

                <CottageContactPriceResponse
                  v-if="pricesResponse"
                  :item="pricesResponse"
                />
              </UCard>
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template
            v-if="
              [
                STATES.LOAD_SUCCESS,
                STATES.CREATING,
                STATES.GETTING_PRICES,
                STATES.CREATE_ERROR,
              ].some(state.matches)
            "
          >
            <div v-if="cottageContactPage">
              <CottageContactForm
                :item="cottageContactPage"
                @submit="onSubmit"
              />
              <TText size="sm" color="gray600" classes="mt-4">
                {{ t('views.cottage-contact.underFormMessage') }}
              </TText>
            </div>
          </template>

          <CottageContactPageSuccess
            v-if="state.matches(STATES.CREATE_SUCCESS) && cottageContactPage"
            :id="cottageContactPage.id"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// State Machine
import { useMachine } from '@xstate/vue'
import fsm, {
  STATES,
  TRANSITIONS,
} from '../utils/stateMachines/cottageContactPageForm.fsm'

// Composables
import { useCottageContactPageEmail } from '../composables/useCottageContactPageEmail'
import { useCottageContactPagePrices } from '../composables/useCottageContactPagePrices'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import { sendCottageContact } from '~/modules/integrations/hotjar/composables/useHotjarComposable'

// Entities
import type { CottageContactPage } from '../entities/CottageContactPage'
import type {
  CottageContactPageCreateForm,
  CottageContactPageCreateResponse,
} from '../entities/CottageContactPageCreate'
import type {
  CottageContactPagePricesForm,
  CottageContactPagePricesDates,
  CottageContactPagePricesPeople,
  CottageContactPagePricesResponse,
} from '../entities/CottageContactPagePrices'

// Design System
import { publicCard } from '~/modules/design-system/utils/nuxt-ui-config/components/card'

// Composables
const { sendFormEmailData } = useCottageContactPageEmail()
const { getPricesData } = useCottageContactPagePrices()
const { setCookieRuralityCottageContact } = useCookiesRurality()
const { state, send } = useMachine(fsm)

// Props
interface propsInterface {
  cottageContactPage: CottageContactPage
}
const props = defineProps<propsInterface>()

// Constants
let formError: ApiResponseError | null = null
const dates = ref<CottageContactPagePricesDates | null>(null)
const people = ref<CottageContactPagePricesPeople | null>(null)

const pricesResponse = ref<CottageContactPagePricesResponse | null>(null)

// Methods
const onPriceSubmit = async (ev: CottageContactPagePricesForm) => {
  send(TRANSITIONS.onGetPrices)
  if (props.cottageContactPage) {
    const responseGetPricesData = await getPricesData({
      cottageContactPage: props.cottageContactPage,
      dates: ev.dates,
      people: ev.people,
    })
    pricesResponse.value = responseGetPricesData
  }
  send(TRANSITIONS.onGetPricesResult)
}

const onPriceUpdateDates = async (ev: CottageContactPagePricesDates) => {
  dates.value = ev
}

const onPriceUpdatePeople = async (ev: CottageContactPagePricesPeople) => {
  people.value = ev
}

const onSubmit = async (ev: CottageContactPageCreateForm) => {
  send(TRANSITIONS.onCreate)
  if (props.cottageContactPage) {
    const responseSendFormData = await sendFormEmailData({
      createForm: ev,
      cottageContactPage: props.cottageContactPage,
      dates: dates.value,
      people: people.value,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      send(TRANSITIONS.onCreateError)
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  cottageContactPageCreateResponse: CottageContactPageCreateResponse,
  formData: CottageContactPageCreateForm,
) => {
  if (props.cottageContactPage) {
    setDataRepository(formData)

    sendGa4ContactEvent({
      province: props.cottageContactPage.province.name,
      method: 'email',
    })
    sendCottageConversionAll()
    sendCottageContact()
    if (cottageContactPageCreateResponse) {
      sendAdsContact({
        email: cottageContactPageCreateResponse.traveler.email,
        phone: cottageContactPageCreateResponse.traveler.phone,
      })
    }

    send(TRANSITIONS.onCreateSuccess)
  }
}

const setDataRepository = (formData: CottageContactPageCreateForm) => {
  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}
</script>
